import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, LogOut, Shield, FileText, FileDown } from 'lucide-react';
import { EnPowerLogoText } from '../EnPowerLogo';
import { abmelden, getSupabaseClient } from '../../lib/supabaseClient';
import { machVertragsHtmlEigenstaendig } from '../../lib/eigenstaendigesHtml';

const TEMPLATES = {
  ci: {
    label: 'EN:POWER CI',
    htmlPath: '/contracts/Vermittlervertrag_ENPOWER_CI.html',
    pdfPath: '/contracts/Vermittlervertrag_ENPOWER_CI.pdf',
  },
  druckversion: {
    label: 'EN:POWER Druckversion',
    htmlPath: '/contracts/Vermittlervertrag_ENPOWER_CI_Druckversion.html',
    pdfPath: '/contracts/Vermittlervertrag_ENPOWER_CI_Druckversion.pdf',
  },
};

const CONTRACT_LOGO_PATH = '/enpower-logo-transparent.png';
const AUFTRAGGEBER_ORT = 'Magdeburg';
const CONTRACT_GENERATOR_VERSION = '2026-05-05-contract-examples-v2';

function getGermanCreationDate() {
  return new Date().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const DEFAULT_FORM = {
  anrede: 'Herr',
  vorname: '',
  nachname: '',
  firma: '',
  adresse: '',
  plzOrt: '',
  vertriebsnummer: '',
  vertragsnummer: '',
  ort: '',
  datum: getGermanCreationDate(),
};

const DEFAULT_POLICY = {
  maxContractsPerMonth: 25,
  canEditHtml: true,
  canDownloadGeneratedHtml: true,
  canDownloadGeneratedPdf: true,
  canDownloadOriginalHtmlTemplate: false,
  canDownloadOriginalPdfTemplate: false,
  canEditCommissions: false,
  allowedTemplateVariants: {
    ci: true,
    druckversion: true,
  },
};

const DEFAULT_COMMISSION_RATES = {
  roofPachtMaxEurPerKwp: 220,
  roofPachtExampleOfferedEurPerKwp: 170,
  postTerminationEurPerKwp: 30,
  bessStage1EurPerMw: 2000,
  bessStage2EurPerMw: 8000,
  bessStage3EurPerMw: 12000,
  ffpvContractSuitableEurPerHa: 1000,
  ffpvContractRestrictedEurPerHa: 500,
  ffpvContractUpgradeEurPerHa: 500,
  ffpvGridPositiveEurPerHa: 500,
  ffpvGridHalfEurPerHa: 250,
  ffpvPlanningEurPerHa: 500,
  ffpvExtensionEurPerHa: 1000,
  ffpvRetentionPercent: 10,
  renovationEurPerKwp: 30,
  smallStorageMarketingContingentEurPerUnit: 30000,
  smallStorageExampleOwnerCompensationEur: 70000,
};

const COMMISSION_RATE_SECTIONS = [
  {
    title: 'Dach-PV',
    fields: [
      { key: 'roofPachtMaxEurPerKwp', label: 'Max. Pachtsatz', suffix: '€/kWp' },
      { key: 'roofPachtExampleOfferedEurPerKwp', label: 'Beispiel-Pacht', suffix: '€/kWp' },
      { key: 'postTerminationEurPerKwp', label: 'Nachvergütung', suffix: '€/kWp' },
    ],
  },
  {
    title: 'BESS',
    fields: [
      { key: 'bessStage1EurPerMw', label: 'Stufe 1', suffix: '€/MW' },
      { key: 'bessStage2EurPerMw', label: 'Stufe 2', suffix: '€/MW' },
      { key: 'bessStage3EurPerMw', label: 'Stufe 3', suffix: '€/MW' },
    ],
  },
  {
    title: 'Freiflächen-PV',
    fields: [
      { key: 'ffpvContractSuitableEurPerHa', label: 'Vertrag geeignet', suffix: '€/ha' },
      { key: 'ffpvContractRestrictedEurPerHa', label: 'Vertrag eingeschränkt', suffix: '€/ha' },
      { key: 'ffpvContractUpgradeEurPerHa', label: 'Upgrade geeignet', suffix: '€/ha' },
      { key: 'ffpvGridPositiveEurPerHa', label: 'Positive Netzauskunft', suffix: '€/ha' },
      { key: 'ffpvGridHalfEurPerHa', label: 'Netzauskunft 50%', suffix: '€/ha' },
      { key: 'ffpvPlanningEurPerHa', label: 'Aufstellungsbeschluss', suffix: '€/ha' },
      { key: 'ffpvExtensionEurPerHa', label: 'Flächenerweiterung', suffix: '€/ha' },
      { key: 'ffpvRetentionPercent', label: 'Einbehalt', suffix: '%' },
    ],
  },
  {
    title: 'Sanierung',
    fields: [
      { key: 'renovationEurPerKwp', label: 'Provision', suffix: '€/kWp' },
    ],
  },
  {
    title: 'Kleinspeicher',
    fields: [
      { key: 'smallStorageMarketingContingentEurPerUnit', label: 'Vermarktungskontingent', suffix: '€/Einheit' },
      { key: 'smallStorageExampleOwnerCompensationEur', label: 'Beispiel Eigentümervergütung', suffix: '€' },
    ],
  },
];

const UTF8_BOM = '\uFEFF';

function ensureUtf8Html(html) {
  const source = String(html || '');

  if (/<meta\s+charset=/i.test(source)) {
    return source;
  }

  if (/<head([^>]*)>/i.test(source)) {
    return source.replace(/<head([^>]*)>/i, '<head$1><meta charset="utf-8">');
  }

  return `<!doctype html><html><head><meta charset="utf-8"></head><body>${source}</body></html>`;
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function stripEditorBodyClasses(html) {
  return String(html || '').replace(/<body\b([^>]*)>/i, (match, attributes = '') => {
    let nextAttributes = attributes.replace(/\sclass=(['"])(.*?)\1/i, (_classMatch, quote, classValue) => {
      const classes = classValue
        .split(/\s+/)
        .filter((className) => className && className !== 'editor-mode' && className !== 'editor-move-mode');

      return classes.length ? ` class=${quote}${classes.join(' ')}${quote}` : '';
    });

    nextAttributes = nextAttributes.replace(/\sdata-editor-[\w-]+=(['"])[\s\S]*?\1/gi, '');
    return `<body${nextAttributes}>`;
  });
}

function getContractLogoUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return new URL(CONTRACT_LOGO_PATH, window.location.origin).href;
  }

  return CONTRACT_LOGO_PATH;
}

function replaceContractLogoSources(html) {
  const logoUrl = getContractLogoUrl();

  return String(html || '')
    .replace(/\bsrc=(['"])(?:\.\/)?enpower-logo-transparent\.png\1/gi, `src="${logoUrl}"`)
    .replace(/\bsrc=(['"])\/enpower-logo-transparent\.png\1/gi, `src="${logoUrl}"`)
    .replace(/<img\b([^>]*?)\bsrc=(['"])(.*?)\2([^>]*?\balt=(['"])EN:POWER\5[^>]*)>/gi, (match, beforeSrc, quote, _src, afterSrc) => `<img${beforeSrc}src=${quote}${logoUrl}${quote}${afterSrc}>`)
    .replace(/<img\b([^>]*?\balt=(['"])EN:POWER\2[^>]*?)\bsrc=(['"])(.*?)\3([^>]*)>/gi, (match, beforeSrc, _altQuote, quote, _src, afterSrc) => `<img${beforeSrc}src=${quote}${logoUrl}${quote}${afterSrc}>`);
}

function addGeneratedPrintFixes(html) {
  return String(html || '');
}

function isPaginationScript(script) {
  return /<script\b[^>]*\bsrc=(['"])(?:[^'"]*\/)?contract-pagination\.js(?:\?[^'"]*)?\1/i.test(String(script || ''))
    || /window\.__ENPOWER_PAGINATED\s*=\s*true|function\s+paginateContract|window\.__ENPOWER_REPAGINATE\s*=\s*paginateContract/i.test(String(script || ''));
}

function prepareGeneratedContractHtml(html) {
  const prepared = stripEditorBodyClasses(replaceContractLogoSources(String(html || '')))
    .replace(/\scontenteditable=(['"])(?:true|false)\1/gi, '')
    .replace(/\sdraggable=(['"])(?:true|false)\1/gi, '')
    .replace(/\sdata-editor-[\w-]+=(['"])[\s\S]*?\1/gi, '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, (script) => (isPaginationScript(script) ? script : ''));

  return addGeneratedPrintFixes(prepared);
}

function buildVermittlerName(form) {
  return [form.anrede, form.vorname, form.nachname].filter(Boolean).join(' ').trim();
}

function normalizeCommissionRates(rates = {}) {
  const source = rates && typeof rates === 'object' ? rates : {};

  return Object.fromEntries(
    Object.entries(DEFAULT_COMMISSION_RATES).map(([key, fallback]) => {
      const value = Number(source[key]);
      return [key, Number.isFinite(value) && value >= 0 ? value : fallback];
    })
  );
}

function formatGermanNumber(value, decimals = 0) {
  return Number(value || 0).toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatEuroAmount(value, decimals = 0) {
  return `${formatGermanNumber(value, decimals)} €`;
}

function formatPercentValue(value) {
  return Number(value || 0).toLocaleString('de-DE', {
    maximumFractionDigits: 2,
  });
}

function buildContractTemplateUrl(path) {
  const separator = String(path || '').includes('?') ? '&' : '?';
  return `${path}${separator}v=${encodeURIComponent(CONTRACT_GENERATOR_VERSION)}`;
}

const HTML_ENTITY_MAP = {
  auml: 'ä',
  Auml: 'Ä',
  ouml: 'ö',
  Ouml: 'Ö',
  uuml: 'ü',
  Uuml: 'Ü',
  szlig: 'ß',
  euro: '€',
  times: '×',
  sect: '§',
  nbsp: ' ',
  amp: '&',
};

function decodeHtmlText(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCodePoint(parseInt(code, 16)))
    .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(parseInt(code, 10)))
    .replace(/&([a-z][a-z0-9]+);/gi, (match, entity) => HTML_ENTITY_MAP[entity] || match)
    .replace(/\s+/g, ' ')
    .trim();
}

function replaceParagraphText(html, matcher, nextText) {
  const testMatcher = typeof matcher === 'function'
    ? matcher
    : (text) => matcher.test(text);

  return String(html || '').replace(/<p\b([^>]*)>([\s\S]*?)<\/p>/gi, (match, attributes, content) => {
    const text = decodeHtmlText(content);
    return testMatcher(text) ? `<p${attributes}>${nextText}</p>` : match;
  });
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function updateElementContentByEditorId(html, editorId, updater) {
  if (!editorId || typeof updater !== 'function') {
    return { html: String(html || ''), updated: false };
  }

  const editorIdPattern = escapeRegExp(editorId);
  const elementPattern = new RegExp(`<([a-z][\\w:-]*)\\b([^>]*\\bdata-editor-id=(['"])${editorIdPattern}\\3[^>]*)>([\\s\\S]*?)<\\/\\1>`, 'gi');
  let updated = false;
  const output = String(html || '').replace(elementPattern, (match, tagName, attributes, _quote, content) => {
    const nextContent = updater(content, decodeHtmlText(content));
    if (typeof nextContent !== 'string') {
      return match;
    }

    updated = true;
    return `<${tagName}${attributes}>${nextContent}</${tagName}>`;
  });

  return { html: output, updated };
}

function serializeEditorState(state) {
  return JSON.stringify(state)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

function updateEditorStateBlock(html, editorId, updater) {
  if (!editorId || typeof updater !== 'function') {
    return { html: String(html || ''), updated: false };
  }

  let updated = false;
  const output = String(html || '').replace(/<script\b([^>]*\bid=(['"])enpower-editor-state\2[^>]*)>([\s\S]*?)<\/script>/i, (match, attributes, _quote, stateJson) => {
    let state;
    try {
      state = JSON.parse(stateJson);
    } catch (_error) {
      return match;
    }

    const block = state?.blocks?.[editorId];
    if (!block || typeof block.html !== 'string') {
      return match;
    }

    const nextHtml = updater(block.html, decodeHtmlText(block.html));
    if (typeof nextHtml !== 'string') {
      return match;
    }

    block.html = nextHtml;
    updated = true;
    return `<script${attributes}>${serializeEditorState(state)}</script>`;
  });

  return { html: output, updated };
}

function updateCommissionBlock(html, editorId, updater, fallbackMatcher, fallbackText) {
  const elementResult = updateElementContentByEditorId(html, editorId, updater);
  const stateResult = updateEditorStateBlock(elementResult.html, editorId, updater);
  const updated = elementResult.updated || stateResult.updated;

  if (updated || !fallbackMatcher || typeof fallbackText !== 'string') {
    return stateResult.html;
  }

  return replaceParagraphText(stateResult.html, fallbackMatcher, fallbackText);
}

function replaceCommissionParagraph(html, editorId, matcher, nextText) {
  return updateCommissionBlock(html, editorId, () => nextText, matcher, nextText);
}

function updateRoofExampleContent(content, rates, roofDiff, roofExampleTotal) {
  return String(content || '')
    .replace(/(Angebotene Pacht:\s*)[\d.,]+\s*€\s*\/\s*kWp/i, `$1${formatEuroAmount(rates.roofPachtExampleOfferedEurPerKwp)}/kWp`)
    .replace(/(Differenz:\s*)[\d.,]+\s*€\s*\/\s*kWp/i, `$1${formatEuroAmount(roofDiff)}/kWp`)
    .replace(/(200\s*kWp\s*×\s*)[\d.,]+\s*€(\s*=\s*)[\d.,]+\s*€(\s*Provision)/i, `$1${formatEuroAmount(roofDiff)}$2${formatEuroAmount(roofExampleTotal)}$3`);
}

function updateBessExampleContent(content, bessStage2ExampleTotal) {
  return String(content || '')
    .replace(/(beträgt die Provision\s*)[\d.,]+\s*€/i, `$1${formatEuroAmount(bessStage2ExampleTotal)}`)
    .replace(/(Stufe 2\s*[—-]\s*)[\d.,]+\s*€/i, `$1${formatEuroAmount(bessStage2ExampleTotal)}`);
}

function updateFfpvExampleContent(content, rates, totals) {
  return String(content || '')
    .replace(/(20\s*ha\s*×\s*)[\d.,]+\s*€(\s*=\s*)[\d.,]+\s*€(\s*Provision)/i, `$1${formatEuroAmount(rates.ffpvContractSuitableEurPerHa, 2)}$2${formatEuroAmount(totals.ffpvExampleTotal, 2)}$3`)
    .replace(/(Einbehalt:\s*)[\d.,]+\s*%(\s*von\s*)[\d.,]+\s*€(\s*=\s*)[\d.,]+\s*€/i, `$1${formatPercentValue(rates.ffpvRetentionPercent)} %$2${formatEuroAmount(totals.ffpvExampleTotal, 2)}$3${formatEuroAmount(totals.ffpvRetention, 2)}`)
    .replace(/(Auszahlungsbetrag nach Einbehalt:\s*)[\d.,]+\s*€/i, `$1${formatEuroAmount(totals.ffpvPayout, 2)}`);
}

function updateRenovationExampleContent(content, rates, renovationExampleKwp, renovationExampleTotal) {
  return String(content || '').replace(
    /(300\s*kWp\s*×\s*)[\d.,]+\s*€(\s*=\s*)[\d.,]+\s*€(\s*Provision)/i,
    `$1${formatEuroAmount(rates.renovationEurPerKwp)}$2${formatEuroAmount(renovationExampleTotal)}$3`
  );
}

function updateSmallStorageExampleContent(content, storageExample) {
  return String(content || '')
    .replace(/(0,75\s*MW\s*\(3 Einheiten\)\s*=\s*)[\d.,]+\s*€(\s*Kontingent)/i, `$1${formatEuroAmount(storageExample.contingentTotal)}$2`)
    .replace(/(Kontingent\s*[–-]\s*)[\d.,]+\s*€(\s*Eigentümervergütung)/i, `$1${formatEuroAmount(storageExample.ownerCompensation)}$2`)
    .replace(/(Eigentümervergütung\s*=\s*)-?[\d.,]+\s*€(\s*Provision)/i, `$1${formatEuroAmount(storageExample.provision)}$2`);
}

function assertCommissionExamplesMapped(html, commissionRates) {
  const rates = normalizeCommissionRates(commissionRates);
  const roofDiff = Math.max(0, rates.roofPachtMaxEurPerKwp - rates.roofPachtExampleOfferedEurPerKwp);
  const expectedValues = [
    formatEuroAmount(roofDiff * 200),
    formatEuroAmount(rates.bessStage2EurPerMw * 10),
    formatEuroAmount(rates.ffpvContractSuitableEurPerHa * 20, 2),
    formatEuroAmount((rates.ffpvContractSuitableEurPerHa * 20) * (1 - (rates.ffpvRetentionPercent / 100)), 2),
    formatEuroAmount(rates.renovationEurPerKwp * 300),
    formatEuroAmount((rates.smallStorageMarketingContingentEurPerUnit * 3) - rates.smallStorageExampleOwnerCompensationEur),
  ];
  const text = decodeHtmlText(html);
  const missingValues = expectedValues.filter((expectedValue) => expectedValue && !text.includes(expectedValue));

  if (missingValues.length > 0) {
    throw new Error(`Vertragsbeispiele konnten nicht vollständig aktualisiert werden. Bitte Seite neu laden und erneut erzeugen. Fehlende Werte: ${missingValues.join(', ')}`);
  }
}

function injectAuftraggeberSignatureData(html, creationDate) {
  const date = String(creationDate || '').trim();
  if (!date) {
    return String(html || '');
  }

  const signatureLabel = `${AUFTRAGGEBER_ORT}, ${date}, Unterschrift Auftraggeber`;

  return String(html || '')
    .replace(
      /<p\b([^>]*)>\s*Ort,\s*Datum,\s*Unterschrift Auftraggeber\s*<\/p>/gi,
      `<p$1>${escapeHtml(signatureLabel)}</p>`
    )
    .replace(
      /\\u003cp>Ort,\s*Datum,\s*Unterschrift Auftraggeber\\u003c\/p>/gi,
      `\\u003cp>${signatureLabel}\\u003c/p>`
    );
}

function injectCommissionData(html, commissionRates) {
  const rates = normalizeCommissionRates(commissionRates);
  const roofDiff = Math.max(0, rates.roofPachtMaxEurPerKwp - rates.roofPachtExampleOfferedEurPerKwp);
  const roofExampleTotal = roofDiff * 200;
  const bessStage2ExampleTotal = rates.bessStage2EurPerMw * 10;
  const ffpvExampleHa = 20;
  const ffpvExampleTotal = rates.ffpvContractSuitableEurPerHa * ffpvExampleHa;
  const ffpvRetention = ffpvExampleTotal * (rates.ffpvRetentionPercent / 100);
  const ffpvPayout = ffpvExampleTotal - ffpvRetention;
  const renovationExampleKwp = 300;
  const renovationExampleTotal = rates.renovationEurPerKwp * renovationExampleKwp;
  const smallStorageExampleUnits = 3;
  const smallStorageExample = {
    contingentTotal: rates.smallStorageMarketingContingentEurPerUnit * smallStorageExampleUnits,
    ownerCompensation: rates.smallStorageExampleOwnerCompensationEur,
  };
  smallStorageExample.provision = smallStorageExample.contingentTotal - smallStorageExample.ownerCompensation;

  let output = String(html || '');
  output = replaceCommissionParagraph(output, 'block-102', /eine feste Nachvergütung von\s*[\d.,]+\s*€\s*pro kWp/i, `Der Vermittler behält Anspruch auf verdiente Provisionen für solche Projekte, bei denen er zum Zeitpunkt der Kündigung sämtliche vertraglichen Pflichten gemäß §§ 2.2 oder 2.3 vollständig erfüllt hat und die innerhalb von 6 Monaten nach Vertragsende zum Abschluss kommen. Für Projekte des Kundenstamms des Vermittlers, die nach diesem Zeitraum zustande kommen, gilt eine feste Nachvergütung von ${formatEuroAmount(rates.postTerminationEurPerKwp)} pro kWp.`);
  output = replaceCommissionParagraph(output, 'block-123', /Der Vermittler kann bis zu\s*[\d.,]+\s*€\s*pro kWp Pacht anbieten/i, `Ein Provisionsanspruch entsteht nach erfolgreicher Umsetzung und Abnahme eines PV-Pachtprojekts sowie vollständigem Zahlungseingang beim Auftraggeber. Der Vermittler kann bis zu ${formatEuroAmount(rates.roofPachtMaxEurPerKwp)} pro kWp Pacht anbieten; die Differenz zwischen dem maximalen Pachtsatz und dem angebotenen Pachtsatz multipliziert mit der kWp-Leistung ist die Provision.`);
  output = updateCommissionBlock(
    output,
    'block-124',
    (content) => updateRoofExampleContent(content, rates, roofDiff, roofExampleTotal),
    (text) => /Rechenbeispiel:\s*Angebotene Pacht:/i.test(text) && /200 kWp/i.test(text),
    `Rechenbeispiel: Angebotene Pacht: ${formatEuroAmount(rates.roofPachtExampleOfferedEurPerKwp)}/kWp | Differenz: ${formatEuroAmount(roofDiff)}/kWp | 200 kWp × ${formatEuroAmount(roofDiff)} = ${formatEuroAmount(roofExampleTotal)} Provision`
  );
  output = replaceCommissionParagraph(output, 'block-127', /Stufe 1 – Netzanschlussgenehmigung liegt zum Verkaufszeitpunkt vor:/i, `Stufe 1 – Netzanschlussgenehmigung liegt zum Verkaufszeitpunkt vor: ${formatEuroAmount(rates.bessStage1EurPerMw)} pro MW`);
  output = replaceCommissionParagraph(output, 'block-128', /Stufe 2 – Baugenehmigung liegt zum Verkaufszeitpunkt vor:/i, `Stufe 2 – Baugenehmigung liegt zum Verkaufszeitpunkt vor: ${formatEuroAmount(rates.bessStage2EurPerMw)} pro MW`);
  output = replaceCommissionParagraph(output, 'block-129', /Stufe 3 – Inbetriebnahme ist zum Verkaufszeitpunkt erfolgt:/i, `Stufe 3 – Inbetriebnahme ist zum Verkaufszeitpunkt erfolgt: ${formatEuroAmount(rates.bessStage3EurPerMw)} pro MW`);
  output = updateCommissionBlock(
    output,
    'block-134',
    (content) => updateBessExampleContent(content, bessStage2ExampleTotal),
    (text) => /Beispiel: Liegt beim Verkauf die Baugenehmigung vor/i.test(text) && /keine Addition/i.test(text),
    `Beispiel: Liegt beim Verkauf die Baugenehmigung vor, beträgt die Provision ${formatEuroAmount(bessStage2ExampleTotal)}. Wird die Anlage nach dem Verkauf in Betrieb genommen, entsteht daraus kein weiterer Anspruch. Lagen beim Verkauf sowohl Netzanschluss- als auch Baugenehmigung vor, gilt ausschließlich Stufe 2 — ${formatEuroAmount(bessStage2ExampleTotal)}, keine Addition.`
  );
  output = replaceCommissionParagraph(output, 'block-138', /auf einer als geeignet eingestuften Fläche erhält der Vermittler/i, `Bei Abschluss eines Nutzungs-, Miet- oder Pachtvertrages zwischen dem Auftraggeber, einem vom Auftraggeber benannten Projektpartner oder einer Gesellschaft der Auftraggeber-Gruppe und dem Eigentümer auf einer als geeignet eingestuften Fläche erhält der Vermittler ${formatEuroAmount(rates.ffpvContractSuitableEurPerHa, 2)} pro qualifiziertem, vom Vertrag umfassten Hektar.`);
  output = replaceCommissionParagraph(output, 'block-139', /auf einer als eingeschränkt geeignet eingestuften Fläche erhält der Vermittler/i, `Bei Abschluss eines solchen Vertrages auf einer als eingeschränkt geeignet eingestuften Fläche erhält der Vermittler ${formatEuroAmount(rates.ffpvContractRestrictedEurPerHa, 2)} pro qualifiziertem, vom Vertrag umfassten Hektar. Wird diese Fläche durch Erfüllung projektspezifischer Kriterien, insbesondere durch Erreichen einer erforderlichen Mindestgröße im Akquiseprozess, von einer eingeschränkt geeigneten zu einer geeigneten Fläche entwickelt, erhält der Vermittler weitere ${formatEuroAmount(rates.ffpvContractUpgradeEurPerHa, 2)} pro qualifiziertem, vom Vertrag umfassten Hektar.`);
  output = replaceCommissionParagraph(output, 'block-142', /Bei Erhalt einer positiven Netzauskunft des zuständigen Stromnetzbetreibers/i, `Bei Erhalt einer positiven Netzauskunft des zuständigen Stromnetzbetreibers für die auf der Fläche zu entwickelnde PV-Anlage erhält der Vermittler ${formatEuroAmount(rates.ffpvGridPositiveEurPerHa, 2)} pro qualifiziertem, vom Vertrag umfassten Hektar.`);
  output = replaceCommissionParagraph(output, 'block-145', /Meilenstein II zu 50 % als erfüllt; der Vermittler erhält/i, `Liegt der Netzverknüpfungspunkt außerhalb des Maximalradius, gilt das Vorhaben zunächst als unwirtschaftlich und Meilenstein II als nicht erreicht. Bemüht sich der Auftraggeber um einen alternativen Netzverknüpfungspunkt innerhalb des Maximalradius, gilt Meilenstein II zu 50 % als erfüllt; der Vermittler erhält in diesem Fall ${formatEuroAmount(rates.ffpvGridHalfEurPerHa, 2)} pro qualifiziertem, vom Vertrag umfassten Hektar. Stellt der Vermittler selbst Erweiterungsflächen bereit, die den Maximalradius erweitern, oder vermittelt er einen alternativen Netzverknüpfungspunkt, gilt Meilenstein II wieder als voll erreicht.`);
  output = replaceCommissionParagraph(output, 'block-148', /erhält der Vermittler [\d.,]+\s*€ pro qualifiziertem Hektar, der vom Vertrag und vom Aufstellungsbeschluss/i, `Bei Erteilung eines Aufstellungsbeschlusses der Gemeinde oder positiv beantworteter Bauvoranfrage im Fall eines privilegierten Vorhabens im Sinne des § 35 BauGB (Positivbescheid) zugunsten des Auftraggebers, eines vom Auftraggeber benannten Projektpartners oder einer Gesellschaft der Auftraggeber-Gruppe erhält der Vermittler ${formatEuroAmount(rates.ffpvPlanningEurPerHa, 2)} pro qualifiziertem Hektar, der vom Vertrag und vom Aufstellungsbeschluss oder vom Positivbescheid umfasst ist.`);
  output = replaceCommissionParagraph(output, 'block-153', /im Rahmen einer solchen Flächenerweiterung erhält der Vermittler/i, `Bei Abschluss eines Nutzungs-, Miet- oder Pachtvertrages im Rahmen einer solchen Flächenerweiterung erhält der Vermittler ${formatEuroAmount(rates.ffpvExtensionEurPerHa, 2)} pro qualifiziertem, vom Vertrag umfassten Hektar.`);
  output = replaceCommissionParagraph(output, 'block-165', /einen Anteil in Höhe von [\d.,]+\s*% der jeweils entstehenden Provision/i, `Der Auftraggeber ist berechtigt, einen Anteil in Höhe von ${formatPercentValue(rates.ffpvRetentionPercent)} % der jeweils entstehenden Provision des Vermittlers einzubehalten. Dieser Einbehalt dient ausschließlich dem Aufbau und der Unterstützung der Vertriebsaktivitäten des Vermittlers durch die Generierung von qualifizierten Leads.`);
  output = updateCommissionBlock(
    output,
    'block-170',
    (content) => updateFfpvExampleContent(content, rates, { ffpvExampleTotal, ffpvRetention, ffpvPayout }),
    /Provisionsbeispiel:\s*20 ha Projektfläche/i,
    `Provisionsbeispiel: 20 ha Projektfläche. Als geeignet eingestuft: 20 ha × ${formatEuroAmount(rates.ffpvContractSuitableEurPerHa, 2)} = ${formatEuroAmount(ffpvExampleTotal, 2)} Provision. Einbehalt: ${formatPercentValue(rates.ffpvRetentionPercent)} % von ${formatEuroAmount(ffpvExampleTotal, 2)} = ${formatEuroAmount(ffpvRetention, 2)}. Auszahlungsbetrag nach Einbehalt: ${formatEuroAmount(ffpvPayout, 2)}. Meilenstein II und III entstehen erst bei positiver Netzauskunft bzw. Aufstellungsbeschluss oder Positivbescheid.`
  );
  output = replaceCommissionParagraph(output, 'block-172', /Provision:\s*[\d.,]+\s*€ pro installiertem kWp\. Auszahlung/i, `Der Provisionsanspruch entsteht nach erfolgreicher Umsetzung und Abnahme durch den Fachpartner sowie vollständigem Zahlungseingang beim Auftraggeber. Provision: ${formatEuroAmount(rates.renovationEurPerKwp, 2)} pro installiertem kWp. Auszahlung innerhalb von 15 Tagen nach Eingang der Provisionszahlung durch den Fachpartner.`);
  output = updateCommissionBlock(
    output,
    'block-173',
    (content) => updateRenovationExampleContent(content, rates, renovationExampleKwp, renovationExampleTotal),
    /Rechenbeispiel:\s*300 kWp\s*×/i,
    `Rechenbeispiel: ${renovationExampleKwp} kWp × ${formatEuroAmount(rates.renovationEurPerKwp)} = ${formatEuroAmount(renovationExampleTotal)} Provision.`
  );
  output = replaceCommissionParagraph(output, 'block-175', /Vermarktungskontingent von\s*[\d.,]+\s*€/i, `Für Kleinspeicherprojekte (je 0,25 MW / 250 kW) erhält der Vermittler ein Vermarktungskontingent von ${formatEuroAmount(rates.smallStorageMarketingContingentEurPerUnit)} bei einer Laufzeit von 20 Jahren. Die Gesamtprojektgröße darf maximal 1 MW betragen. Die Provision ergibt sich aus der Differenz zwischen dem Gesamtkontingent und der mit dem Eigentümer vereinbarten Gesamtvergütung. Der Provisionsanspruch entsteht ausschließlich nach vollständigem Projektverkauf und vollständigem Zahlungseingang.`);
  output = updateCommissionBlock(
    output,
    'block-176',
    (content) => updateSmallStorageExampleContent(content, smallStorageExample),
    /Rechenbeispiel:\s*0,75 MW\s*\(3 Einheiten\)/i,
    `Rechenbeispiel: 0,75 MW (3 Einheiten) = ${formatEuroAmount(smallStorageExample.contingentTotal)} Kontingent – ${formatEuroAmount(smallStorageExample.ownerCompensation)} Eigentümervergütung = ${formatEuroAmount(smallStorageExample.provision)} Provision.`
  );

  return output;
}

function injectPartnerData(html, form) {
  const vermittlerName = buildVermittlerName(form);
  const anschrift = [form.adresse, form.plzOrt].filter(Boolean).join(', ');
  const vermittlerOrtDatum = [form.ort, form.datum].filter(Boolean).join(', ');
  const auftraggeberDateLine = form.datum ? `${AUFTRAGGEBER_ORT}, den ${form.datum}` : '';

  let output = String(html || '');

  if (vermittlerName) {
    output = output.replace(
      /(Name des Vermittlers:\s*)(?:&nbsp;|\s|<[^>]+>)*_{5,}/gi,
      `$1 ${escapeHtml(vermittlerName)}`
    );
  }

  if (anschrift) {
    output = output.replace(/(Anschrift:\s*)(?:&nbsp;|\s|<[^>]+>)*_{5,}/gi, `$1 ${escapeHtml(anschrift)}`);
  }

  if (form.vertriebsnummer) {
    output = output.replace(/(Vertriebsnummer:\s*)(?:&nbsp;|\s|<[^>]+>)*_{5,}/gi, `$1 ${escapeHtml(form.vertriebsnummer)}`);
  }

  if (form.vertragsnummer) {
    output = output.replace(/(Vertragsnr\.?\s*:?\s*)(?:&nbsp;|\s|<[^>]+>)*_{3,}/gi, `$1${escapeHtml(form.vertragsnummer)}`);
  }

  if (form.datum) {
    output = replaceParagraphText(
      output,
      /Der Vertrag tritt mit Unterzeichnung in Kraft und gilt auf unbestimmte Zeit\. Der Beginn der Vermittlertätigkeit ist:/i,
      `Der Vertrag tritt ab dem Datum der Erstellung in Kraft und gilt auf unbestimmte Zeit. Datum der Erstellung: ${escapeHtml(form.datum)}`
    );
    output = output.replace(/(Beginn der Vermittlertätigkeit ist:\s*)(?:&nbsp;|\s|<[^>]+>)*_{3,}/gi, `$1${escapeHtml(form.datum)}`);
    output = injectAuftraggeberSignatureData(output, form.datum);
  }

  if (auftraggeberDateLine) {
    output = output.replace(
      /(Magdeburg, den\s*)(?:&nbsp;|\s|<[^>]+>)*_{5,}\s*2026/gi,
      `${escapeHtml(auftraggeberDateLine)}`
    );
  }

  if (vermittlerOrtDatum) {
    output = output.replace(/(Ort, Datum, Unterschrift Vermittler:)/gi, `${escapeHtml(vermittlerOrtDatum)}, Unterschrift Vermittler:`);
  }

  return output;
}

function normalizeContractPolicy(policy = {}) {
  const variants = policy.allowedTemplateVariants && typeof policy.allowedTemplateVariants === 'object'
    ? policy.allowedTemplateVariants
    : {};
  const allowedTemplateVariants = {
    ci: variants.ci !== false,
    druckversion: variants.druckversion !== false,
  };

  if (!allowedTemplateVariants.ci && !allowedTemplateVariants.druckversion) {
    allowedTemplateVariants.ci = true;
  }

  return {
    maxContractsPerMonth: Math.max(1, Number(policy.maxContractsPerMonth || DEFAULT_POLICY.maxContractsPerMonth)),
    canEditHtml: policy.canEditHtml !== false,
    canDownloadGeneratedHtml: policy.canDownloadGeneratedHtml !== false,
    canDownloadGeneratedPdf: Object.prototype.hasOwnProperty.call(policy, 'canDownloadGeneratedPdf')
      ? policy.canDownloadGeneratedPdf !== false
      : (Object.prototype.hasOwnProperty.call(policy, 'canDownloadPdfTemplate') ? policy.canDownloadPdfTemplate !== false : true),
    canDownloadOriginalHtmlTemplate: policy.canDownloadOriginalHtmlTemplate === true,
    canDownloadOriginalPdfTemplate: policy.canDownloadOriginalPdfTemplate === true,
    canEditCommissions: policy.canEditCommissions === true,
    allowedTemplateVariants,
  };
}

function getAllowedTemplateKeys(policy = DEFAULT_POLICY) {
  const normalizedPolicy = normalizeContractPolicy(policy);
  return Object.keys(TEMPLATES).filter((templateKey) => normalizedPolicy.allowedTemplateVariants?.[templateKey] === true);
}

function downloadTextFile(filename, content) {
  const blob = new Blob([UTF8_BOM, ensureUtf8Html(content)], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function downloadBlobFile(filename, blob, type) {
  const safeBlob = blob instanceof Blob ? blob : new Blob([blob], { type });
  const url = URL.createObjectURL(safeBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function VermittlervertragStudio({ apiBase = '/api', session: globalSession = null, toolAccess = { canCreate: true, canEdit: true } }) {
  const supabase = useMemo(() => getSupabaseClient(), []);
  const [authLoading, setAuthLoading] = useState(true);
  const [localSession, setLocalSession] = useState(null);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [activeTemplate, setActiveTemplate] = useState('ci');
  const [form, setForm] = useState(DEFAULT_FORM);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState(null);
  const [contractUsers, setContractUsers] = useState([]);
  const [usersNotice, setUsersNotice] = useState('');
  const [defaultPolicyDraft, setDefaultPolicyDraft] = useState(DEFAULT_POLICY);
  const [userPoliciesDraft, setUserPoliciesDraft] = useState({});
  const [newPolicyEmail, setNewPolicyEmail] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [commissionRates, setCommissionRates] = useState(DEFAULT_COMMISSION_RATES);

  const session = globalSession || localSession;

  const accessToken = session?.access_token || '';

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${accessToken}`,
  }), [accessToken]);

  const effectivePolicy = useMemo(() => normalizeContractPolicy(settings?.policy || DEFAULT_POLICY), [settings?.policy]);
  const allowedTemplateKeys = useMemo(() => getAllowedTemplateKeys(effectivePolicy), [effectivePolicy]);
  const canCreateByTool = toolAccess?.canCreate !== false;
  const canEditByTool = toolAccess?.canEdit !== false;
  const canEditGeneratedHtml = canEditByTool && effectivePolicy.canEditHtml;
  const canDownloadGeneratedHtml = canCreateByTool && effectivePolicy.canDownloadGeneratedHtml;
  const canDownloadGeneratedPdf = canCreateByTool && effectivePolicy.canDownloadGeneratedPdf;
  const canDownloadOriginalHtmlTemplate = canCreateByTool && effectivePolicy.canDownloadOriginalHtmlTemplate;
  const canDownloadOriginalPdfTemplate = canCreateByTool && effectivePolicy.canDownloadOriginalPdfTemplate;
  const canEditCommissionRates = settings?.isAdmin || effectivePolicy.canEditCommissions;

  const canGenerateContracts = canCreateByTool && (usage?.remaining ?? effectivePolicy.maxContractsPerMonth) > 0;

  useEffect(() => {
    setCommissionRates(DEFAULT_COMMISSION_RATES);
  }, [settings?.email]);

  useEffect(() => {
    if (!allowedTemplateKeys.includes(activeTemplate)) {
      setActiveTemplate(allowedTemplateKeys[0] || 'ci');
    }
  }, [activeTemplate, allowedTemplateKeys]);

  const refreshSettings = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    setSettingsLoading(true);
    try {
      const response = await axios.get(`${apiBase}/contracts/settings`, {
        headers: authHeaders,
      });
      setSettings(response.data);
      setDefaultPolicyDraft(response.data.defaultPolicy || DEFAULT_POLICY);
      setUserPoliciesDraft(response.data.userPolicies || {});
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Einstellungen konnten nicht geladen werden.');
    } finally {
      setSettingsLoading(false);
    }
  }, [accessToken, apiBase, authHeaders]);

  const refreshUsage = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    setUsageLoading(true);
    try {
      const response = await axios.get(`${apiBase}/contracts/usage`, {
        headers: authHeaders,
      });
      setUsage(response.data);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Nutzung konnte nicht geladen werden.');
    } finally {
      setUsageLoading(false);
    }
  }, [accessToken, apiBase, authHeaders]);

  const refreshUsers = useCallback(async () => {
    if (!accessToken || !settings?.isAdmin) {
      setContractUsers([]);
      setUsersNotice('');
      return;
    }

    setUsersLoading(true);
    try {
      const response = await axios.get(`${apiBase}/contracts/users`, {
        headers: authHeaders,
      });
      setContractUsers(response.data.users || []);
      setUsersNotice(response.data.detail || '');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Nutzer konnten nicht geladen werden.');
    } finally {
      setUsersLoading(false);
    }
  }, [accessToken, apiBase, authHeaders, settings?.isAdmin]);

  useEffect(() => {
    let isMounted = true;

    if (!supabase) {
      setAuthLoading(false);
      return () => {
        isMounted = false;
      };
    }

    supabase.auth.getSession().then(({ data }) => {
      if (isMounted) {
        setLocalSession(data.session || null);
        setAuthLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      setLocalSession(currentSession || null);
      setAuthLoading(false);
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, [supabase]);

  useEffect(() => {
    if (!accessToken) {
      setSettings(null);
      setUsage(null);
      setContractUsers([]);
      setUsersNotice('');
      return;
    }

    refreshSettings();
    refreshUsage();
  }, [accessToken, refreshSettings, refreshUsage]);

  useEffect(() => {
    refreshUsers();
  }, [refreshUsers]);

  const handleLogin = useCallback(async () => {
    if (!supabase) {
      toast.error('Supabase ist nicht konfiguriert.');
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginData.email.trim(),
        password: loginData.password,
      });

      if (error) {
        throw error;
      }

      toast.success('Login erfolgreich.');
      setLoginData((current) => ({ ...current, password: '' }));
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Login fehlgeschlagen.');
    }
  }, [loginData.email, loginData.password, supabase]);

  const handleLogout = useCallback(async () => {
    if (!supabase) {
      return;
    }

    const { lokalErzwungen, fehler } = await abmelden(supabase);
    setLocalSession(null);
    if (lokalErzwungen) {
      // Supabase kannte die Sitzung nicht mehr (siehe abmelden). Ohne SIGNED_OUT-Ereignis
      // behielte die umgebende App ihre Sitzung - neu laden, dann greift der Login.
      console.warn('Logout: Supabase kannte die Sitzung nicht mehr, lokal beendet.', fehler);
      window.location.reload();
      return;
    }
    toast.success('Logout erfolgreich.');
  }, [supabase]);

  const buildGeneratedHtml = useCallback(async () => {
    const template = TEMPLATES[activeTemplate];
    const response = await fetch(buildContractTemplateUrl(template.htmlPath), { cache: 'no-store' });

    if (!response.ok) {
      throw new Error('Vertragsvorlage konnte nicht geladen werden.');
    }

    const templateHtml = await response.text();
    const creationDate = getGermanCreationDate();
    const withPartnerData = injectPartnerData(templateHtml, { ...form, datum: creationDate });
    const withCommissionData = injectCommissionData(withPartnerData, commissionRates);
    assertCommissionExamplesMapped(withCommissionData, commissionRates);
    const withLogo = replaceContractLogoSources(withCommissionData);

    if (canEditGeneratedHtml) {
      return ensureUtf8Html(withLogo);
    }

    return ensureUtf8Html(prepareGeneratedContractHtml(withLogo));
  }, [activeTemplate, canEditGeneratedHtml, commissionRates, form]);

  const consumeUsage = useCallback(async (format = 'html') => {
    const response = await axios.post(`${apiBase}/contracts/usage`, {
      action: 'generated',
      format,
      templateVariant: activeTemplate,
      contractGeneratorVersion: CONTRACT_GENERATOR_VERSION,
    }, {
      headers: authHeaders,
    });

    setUsage(response.data);
    return response.data;
  }, [activeTemplate, apiBase, authHeaders]);

  const handleGenerateAndDownload = useCallback(async () => {
    if (!session) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }

    if (!canGenerateContracts) {
      toast.error(canCreateByTool ? 'Monatliches Kontingent erreicht. Bitte Admin kontaktieren.' : 'Vertragserstellung ist für diesen Nutzer deaktiviert.');
      return;
    }

    if (!canDownloadGeneratedHtml) {
      toast.error('HTML-Download ist für diesen Nutzer deaktiviert.');
      return;
    }

    setIsGenerating(true);

    try {
      // Eigenständig, sonst zeigt die Datei nur das Deckblatt (12.10.2026).
      const { html, vollstaendig } = await machVertragsHtmlEigenstaendig(
        ensureUtf8Html(prepareGeneratedContractHtml(await buildGeneratedHtml())),
      );
      await consumeUsage('html');

      const safeName = [form.nachname || 'vermittler', form.vertragsnummer || 'vertrag']
        .join('-')
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, '-')
        .replace(/-+/g, '-');

      downloadTextFile(`Vermittlervertrag_${safeName}.html`, html);
      if (vollstaendig) {
        toast.success('Vertrag erzeugt und heruntergeladen.');
      } else {
        toast.warning('Vertrag heruntergeladen. Das Seitenskript ließ sich nicht einbetten – die Datei zeigt alle Seiten nur mit Internetverbindung.');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Vertrag konnte nicht erzeugt werden.');
    } finally {
      setIsGenerating(false);
    }
  }, [buildGeneratedHtml, canCreateByTool, canDownloadGeneratedHtml, canGenerateContracts, consumeUsage, form.nachname, form.vertragsnummer, session]);

  const handleGeneratePdfDownload = useCallback(async () => {
    if (!session) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }

    if (!canGenerateContracts) {
      toast.error(canCreateByTool ? 'Monatliches Kontingent erreicht. Bitte Admin kontaktieren.' : 'Vertragserstellung ist für diesen Nutzer deaktiviert.');
      return;
    }

    if (!canDownloadGeneratedPdf) {
      toast.error('PDF-Download ist für diesen Nutzer deaktiviert.');
      return;
    }

    setIsGenerating(true);

    try {
      const html = ensureUtf8Html(prepareGeneratedContractHtml(await buildGeneratedHtml()));
      const safeName = [form.nachname || 'vermittler', form.vertragsnummer || 'vertrag']
        .join('-')
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, '-')
        .replace(/-+/g, '-');
      const filename = `Vermittlervertrag_${safeName}.pdf`;
      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'generated-pdf',
        html,
        filename,
        templateVariant: activeTemplate,
        contractGeneratorVersion: CONTRACT_GENERATOR_VERSION,
      }, {
        headers: authHeaders,
        responseType: 'blob',
      });

      const used = Number(response.headers?.['x-contract-usage-used']);
      const limit = Number(response.headers?.['x-contract-usage-limit']);
      const remaining = Number(response.headers?.['x-contract-usage-remaining']);
      if (Number.isFinite(used) && Number.isFinite(limit) && Number.isFinite(remaining)) {
        setUsage((current) => ({
          ...(current || {}),
          used,
          limit,
          remaining,
        }));
      } else {
        refreshUsage();
      }

      downloadBlobFile(filename, response.data, 'application/pdf');
      toast.success('Vertrag als PDF erzeugt und heruntergeladen.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'PDF konnte nicht erzeugt werden.');
    } finally {
      setIsGenerating(false);
    }
  }, [activeTemplate, apiBase, authHeaders, buildGeneratedHtml, canCreateByTool, canDownloadGeneratedPdf, canGenerateContracts, form.nachname, form.vertragsnummer, refreshUsage, session]);

  const handleOpenGeneratedHtml = useCallback(async () => {
    if (!canEditGeneratedHtml) {
      toast.error('Bearbeitung ist durch den Admin für diesen Nutzer deaktiviert.');
      return;
    }

    if (!session) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }

    if (!canGenerateContracts) {
      toast.error(canCreateByTool ? 'Monatliches Kontingent erreicht. Bitte Admin kontaktieren.' : 'Vertragserstellung ist für diesen Nutzer deaktiviert.');
      return;
    }

    setIsGenerating(true);

    try {
      // Im neuen Tab (blob:) findet der Browser das relativ eingebundene
      // Seitenskript ebenso wenig wie in einer lokalen Datei.
      const { html, vollstaendig } = await machVertragsHtmlEigenstaendig(ensureUtf8Html(await buildGeneratedHtml()));
      await consumeUsage('editor');
      const blob = new Blob([UTF8_BOM, html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      if (vollstaendig) {
        toast.success('Bearbeitbare Vertrags-HTML geöffnet.');
      } else {
        toast.warning('Vertrags-HTML geöffnet. Das Seitenskript ließ sich nicht einbetten – Seiten erscheinen nur mit Internetverbindung.');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Vertrag konnte nicht geöffnet werden.');
    } finally {
      setIsGenerating(false);
    }
  }, [buildGeneratedHtml, canCreateByTool, canEditGeneratedHtml, canGenerateContracts, consumeUsage, session]);

  const handleSaveAdminSettings = useCallback(async () => {
    if (!settings?.isAdmin) {
      toast.error('Nur Admins dürfen speichern.');
      return;
    }

    try {
      const response = await axios.post(`${apiBase}/contracts/settings`, {
        defaultPolicy: defaultPolicyDraft,
        userPolicies: userPoliciesDraft,
      }, {
        headers: authHeaders,
      });

      setSettings(response.data);
      setDefaultPolicyDraft(response.data.defaultPolicy || DEFAULT_POLICY);
      setUserPoliciesDraft(response.data.userPolicies || {});
      refreshUsers();
      toast.success('Admin-Einstellungen gespeichert.');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Einstellungen konnten nicht gespeichert werden.');
    }
  }, [apiBase, authHeaders, defaultPolicyDraft, refreshUsers, settings?.isAdmin, userPoliciesDraft]);

  const normalizedCommissionRates = useMemo(() => normalizeCommissionRates(commissionRates), [commissionRates]);

  const handleCommissionRateChange = useCallback((key, value) => {
    setCommissionRates((current) => normalizeCommissionRates({
      ...current,
      [key]: value,
    }));
  }, []);

  const activeTemplateConfig = TEMPLATES[activeTemplate];

  if (!supabase) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ maxWidth: '880px', margin: '0 auto', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '22px' }}>
          <EnPowerLogoText size="sm" variant="dark" />
          <h2 style={{ marginTop: '14px', fontFamily: 'Outfit, sans-serif', color: '#122A5C' }}>Vermittlervertrag</h2>
          <p style={{ fontFamily: 'Manrope, sans-serif', color: '#475569' }}>
            Supabase ist noch nicht konfiguriert. Bitte setzen Sie REACT_APP_SUPABASE_URL und REACT_APP_SUPABASE_ANON_KEY im Frontend-Projekt.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 24px 28px' }}>
      <div style={{ maxWidth: '1160px', margin: '0 auto', display: 'grid', gap: '14px' }}>
        <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '16px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <EnPowerLogoText size="sm" variant="dark" />
            <span style={{ color: '#CBD5E1' }}>|</span>
            <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: '16px', fontWeight: 700, color: '#122A5C' }}>Vermittlervertrag Studio</span>
          </div>

          {!globalSession && session ? (
            <button onClick={handleLogout} style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '8px 12px', background: '#FFFFFF', color: '#334155', display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <LogOut size={14} /> Logout
            </button>
          ) : null}
        </div>

        {!globalSession && !session && (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '18px', display: 'grid', gap: '12px', maxWidth: '560px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Lock size={16} color="#122A5C" />
              <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: '#122A5C' }}>Login mit Supabase</h3>
            </div>
            <p style={{ margin: 0, fontFamily: 'Manrope, sans-serif', color: '#64748B', fontSize: '13px' }}>
              Verwenden Sie die bestehenden EN:POWER Login-Daten aus Supabase.
            </p>
            <input
              type="email"
              placeholder="E-Mail"
              value={loginData.email}
              onChange={(event) => setLoginData((current) => ({ ...current, email: event.target.value }))}
              style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '10px 12px', fontFamily: 'Manrope, sans-serif' }}
            />
            <input
              type="password"
              placeholder="Passwort"
              value={loginData.password}
              onChange={(event) => setLoginData((current) => ({ ...current, password: event.target.value }))}
              style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '10px 12px', fontFamily: 'Manrope, sans-serif' }}
            />
            <button
              onClick={handleLogin}
              disabled={authLoading}
              style={{ border: 'none', borderRadius: '10px', padding: '10px 12px', background: '#122A5C', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', fontWeight: 700, cursor: 'pointer' }}
            >
              {authLoading ? 'Prüfe Session...' : 'Einloggen'}
            </button>
            <a href="/auth/forgot-password" style={{ justifySelf: 'start', color: '#122A5C', fontFamily: 'Manrope, sans-serif', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
              Passwort vergessen?
            </a>
          </div>
        )}

        {session && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '18px', display: 'grid', gap: '10px' }}>
                <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: '#122A5C' }}>Vertragspartner-Daten</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input value={form.anrede} onChange={(event) => setForm((current) => ({ ...current, anrede: event.target.value }))} placeholder="Anrede" style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }} />
                  <input value={form.vorname} onChange={(event) => setForm((current) => ({ ...current, vorname: event.target.value }))} placeholder="Vorname" style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }} />
                  <input value={form.nachname} onChange={(event) => setForm((current) => ({ ...current, nachname: event.target.value }))} placeholder="Nachname" style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }} />
                  <input value={form.firma} onChange={(event) => setForm((current) => ({ ...current, firma: event.target.value }))} placeholder="Firma" style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }} />
                  <input value={form.adresse} onChange={(event) => setForm((current) => ({ ...current, adresse: event.target.value }))} placeholder="Adresse" style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }} />
                  <input value={form.plzOrt} onChange={(event) => setForm((current) => ({ ...current, plzOrt: event.target.value }))} placeholder="PLZ / Ort" style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }} />
                  <input value={form.vertriebsnummer} onChange={(event) => setForm((current) => ({ ...current, vertriebsnummer: event.target.value }))} placeholder="Vertriebsnummer" style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }} />
                  <input value={form.vertragsnummer} onChange={(event) => setForm((current) => ({ ...current, vertragsnummer: event.target.value }))} placeholder="Vertragsnummer" style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }} />
                  <input value={form.ort} onChange={(event) => setForm((current) => ({ ...current, ort: event.target.value }))} placeholder="Ort Vermittler" style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }} />
                  <input value={getGermanCreationDate()} readOnly placeholder="Datum der Erstellung" style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px', background: '#F8FAFC', color: '#475569' }} />
                </div>
              </div>

              <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '18px', display: 'grid', gap: '10px' }}>
                <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: '#122A5C' }}>Vorlagen und Aktionen</h3>
                <select
                  value={activeTemplate}
                  onChange={(event) => setActiveTemplate(event.target.value)}
                  style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '10px 12px' }}
                >
                  {Object.entries(TEMPLATES).filter(([key]) => allowedTemplateKeys.includes(key)).map(([key, template]) => (
                    <option key={key} value={key}>{template.label}</option>
                  ))}
                </select>

                {/* Bis zum 23.09.2026 stand hier die ganze Rechteliste (Nutzer,
                    Bearbeitung erlaubt, Erstellung erlaubt, Download, Versionen).
                    Was nicht erlaubt ist, zeigt sich am fehlenden Knopf - wissen
                    muss man nur das Kontingent, und warum Erstellen gesperrt ist. */}
                <div style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#64748B' }}>
                  Diesen Monat: <strong style={{ color: '#122A5C' }}>{usage?.used ?? 0}</strong> von {usage?.limit ?? effectivePolicy.maxContractsPerMonth} Verträgen
                </div>
                {!canCreateByTool ? (
                  <div style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#8A5A00', background: '#FDF8EC', borderLeft: '3px solid #8A5A00', borderRadius: '7px', padding: '7px 10px' }}>
                    Für dein Konto ist das Erstellen nicht freigeschaltet.
                  </div>
                ) : !canGenerateContracts ? (
                  <div style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#8A5A00', background: '#FDF8EC', borderLeft: '3px solid #8A5A00', borderRadius: '7px', padding: '7px 10px' }}>
                    Das Kontingent für diesen Monat ist ausgeschöpft.
                  </div>
                ) : null}

                {(canDownloadOriginalHtmlTemplate || canDownloadOriginalPdfTemplate) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {canDownloadOriginalHtmlTemplate && (
                      <button onClick={() => window.open(activeTemplateConfig.htmlPath, '_blank', 'noopener,noreferrer')} style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 12px', background: '#FFFFFF', cursor: 'pointer' }}>
                        <FileText size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Original-HTML
                      </button>
                    )}

                    {canDownloadOriginalPdfTemplate && (
                    <button onClick={() => window.open(activeTemplateConfig.pdfPath, '_blank', 'noopener,noreferrer')} style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 12px', background: '#FFFFFF', cursor: 'pointer' }}>
                      <FileDown size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} /> Original-PDF
                    </button>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {canDownloadGeneratedHtml && (
                    <button
                      onClick={handleGenerateAndDownload}
                      disabled={isGenerating || usageLoading || !canGenerateContracts}
                      style={{ border: 'none', borderRadius: '10px', padding: '10px 14px', background: '#122A5C', color: '#FFFFFF', cursor: 'pointer', fontWeight: 700 }}
                    >
                      {isGenerating ? 'Erzeuge...' : 'Vertrag als HTML herunterladen'}
                    </button>
                  )}

                  {canDownloadGeneratedPdf && (
                    <button
                      onClick={handleGeneratePdfDownload}
                      disabled={isGenerating || usageLoading || !canGenerateContracts}
                      style={{ border: 'none', borderRadius: '10px', padding: '10px 14px', background: '#0F766E', color: '#FFFFFF', cursor: 'pointer', fontWeight: 700 }}
                    >
                      {isGenerating ? 'Erzeuge...' : 'Vertrag als PDF herunterladen'}
                    </button>
                  )}

                  {canEditGeneratedHtml && (
                    <button
                      onClick={handleOpenGeneratedHtml}
                      disabled={isGenerating || usageLoading || !canGenerateContracts}
                      style={{ border: '1px solid #F59E0B', borderRadius: '10px', padding: '10px 14px', background: '#FFFBEB', color: '#92400E', cursor: 'pointer', fontWeight: 700 }}
                    >
                      Erzeugen und bearbeitbar öffnen
                    </button>
                  )}
                </div>
              </div>
            </div>

            {canEditCommissionRates && (
              <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '18px', display: 'grid', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: '#122A5C' }}>Provisionen</h3>
                  <button
                    type="button"
                    onClick={() => setCommissionRates(DEFAULT_COMMISSION_RATES)}
                    style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '8px 11px', background: '#FFFFFF', color: '#334155', cursor: 'pointer', fontWeight: 700 }}
                  >
                    Standardwerte
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                  {COMMISSION_RATE_SECTIONS.map((section) => (
                    <div key={section.title} style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', display: 'grid', gap: '9px', background: '#FBFDFF' }}>
                      <strong style={{ fontFamily: 'Outfit, sans-serif', fontSize: '13px', color: '#122A5C' }}>{section.title}</strong>
                      {section.fields.map((field) => (
                        <label key={field.key} style={{ display: 'grid', gap: '5px', fontSize: '12px', color: '#475569' }}>
                          <span>{field.label}</span>
                          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '6px', alignItems: 'center' }}>
                            <input
                              type="number"
                              min={0}
                              step={field.suffix === '%' ? 0.1 : 1}
                              value={normalizedCommissionRates[field.key]}
                              onChange={(event) => handleCommissionRateChange(field.key, event.target.value)}
                              style={{ minWidth: 0, border: '1px solid #CBD5E1', borderRadius: '10px', padding: '8px 10px' }}
                            />
                            <span style={{ color: '#64748B', fontSize: '11px', whiteSpace: 'nowrap' }}>{field.suffix}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {false && settings?.isAdmin && (
              <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', padding: '18px', display: 'grid', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Shield size={16} color="#122A5C" />
                  <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: '#122A5C' }}>Admin: Nutzerrechte und Limits</h3>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))', gap: '10px' }}>
                  <label style={{ display: 'grid', gap: '6px', fontSize: '12px', color: '#475569' }}>
                    <span>Max. Vertrags-Erstellungen / Monat</span>
                    <input
                      type="number"
                      min={1}
                      value={defaultPolicyDraft.maxContractsPerMonth}
                      onChange={(event) => setDefaultPolicyDraft((current) => ({
                        ...current,
                        maxContractsPerMonth: Number(event.target.value || 1),
                      }))}
                      style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px' }}
                    />
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
                    <input
                      type="checkbox"
                      checked={defaultPolicyDraft.canEditHtml}
                      onChange={(event) => setDefaultPolicyDraft((current) => ({
                        ...current,
                        canEditHtml: event.target.checked,
                      }))}
                    />
                    Bearbeitung von HTML erlauben
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569' }}>
                    <input
                      type="checkbox"
                      checked={defaultPolicyDraft.canDownloadPdfTemplate}
                      onChange={(event) => setDefaultPolicyDraft((current) => ({
                        ...current,
                        canDownloadPdfTemplate: event.target.checked,
                      }))}
                    />
                    Original-PDF Download erlauben
                  </label>
                </div>

                <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '12px', display: 'grid', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <div>
                      <h4 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: '#122A5C' }}>Nutzerverwaltung</h4>
                      <p style={{ margin: '4px 0 0', fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#64748B' }}>
                        Standardrechte gelten für alle Nutzer ohne Override. Änderungen an einzelnen Nutzern werden als Override gespeichert.
                      </p>
                    </div>
                    <button
                      onClick={refreshUsers}
                      disabled={usersLoading}
                      style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '8px 11px', background: '#FFFFFF', cursor: usersLoading ? 'wait' : 'pointer' }}
                    >
                      {usersLoading ? 'Lädt...' : 'Nutzer aktualisieren'}
                    </button>
                  </div>

                  {usersNotice && (
                    <p style={{ margin: 0, fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '9px 11px' }}>
                      {usersNotice}
                    </p>
                  )}

                  {contractUsers.length === 0 ? (
                    <p style={{ margin: 0, fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#64748B' }}>
                      Noch keine Nutzer geladen.
                    </p>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px', maxHeight: '360px', overflowY: 'auto' }}>
                      {contractUsers.map((user) => {
                        const email = String(user.email || '').trim().toLowerCase();
                        const policy = userPoliciesDraft[email] || defaultPolicyDraft;
                        const hasOverride = Boolean(userPoliciesDraft[email]);

                        return (
                          <div key={email} style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '10px', display: 'grid', gridTemplateColumns: 'minmax(220px, 1.4fr) 150px auto auto auto', gap: '8px', alignItems: 'center' }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '12px', fontWeight: 700, color: '#122A5C', wordBreak: 'break-all' }}>{email}</div>
                              <div style={{ marginTop: '3px', fontSize: '10px', color: '#64748B' }}>
                                {user.source === 'supabase-auth' ? 'Supabase Auth' : 'Tool-Override'}{hasOverride ? ' · Override aktiv' : ' · Standardrechte'}
                              </div>
                            </div>

                            <input
                              type="number"
                              min={1}
                              value={policy.maxContractsPerMonth}
                              onChange={(event) => {
                                const nextValue = Number(event.target.value || 1);
                                setUserPoliciesDraft((current) => ({
                                  ...current,
                                  [email]: {
                                    ...(current[email] || defaultPolicyDraft),
                                    maxContractsPerMonth: nextValue,
                                  },
                                }));
                              }}
                              style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '8px 10px' }}
                            />

                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                              <input
                                type="checkbox"
                                checked={policy.canEditHtml !== false}
                                onChange={(event) => {
                                  setUserPoliciesDraft((current) => ({
                                    ...current,
                                    [email]: {
                                      ...(current[email] || defaultPolicyDraft),
                                      canEditHtml: event.target.checked,
                                    },
                                  }));
                                }}
                              />
                              Bearbeiten
                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                              <input
                                type="checkbox"
                                checked={policy.canDownloadPdfTemplate !== false}
                                onChange={(event) => {
                                  setUserPoliciesDraft((current) => ({
                                    ...current,
                                    [email]: {
                                      ...(current[email] || defaultPolicyDraft),
                                      canDownloadPdfTemplate: event.target.checked,
                                    },
                                  }));
                                }}
                              />
                              PDF
                            </label>

                            <button
                              onClick={() => {
                                setUserPoliciesDraft((current) => {
                                  const next = { ...current };
                                  if (hasOverride) {
                                    delete next[email];
                                  } else {
                                    next[email] = { ...defaultPolicyDraft };
                                  }
                                  return next;
                                });
                              }}
                              style={{ border: hasOverride ? '1px solid #FECACA' : '1px solid #CBD5E1', color: hasOverride ? '#B91C1C' : '#334155', borderRadius: '10px', padding: '8px 10px', background: '#FFFFFF', cursor: 'pointer' }}
                            >
                              {hasOverride ? 'Standard' : 'Override'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '10px', display: 'grid', gap: '8px' }}>
                  <p style={{ margin: 0, fontFamily: 'Manrope, sans-serif', fontSize: '12px', color: '#475569' }}>
                    Optional: nutzerspezifische Overrides
                  </p>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="email"
                      value={newPolicyEmail}
                      onChange={(event) => setNewPolicyEmail(event.target.value)}
                      placeholder="nutzer@firma.de"
                      style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px', minWidth: '280px' }}
                    />
                    <button
                      onClick={() => {
                        const email = String(newPolicyEmail || '').trim().toLowerCase();
                        if (!email) {
                          return;
                        }
                        setUserPoliciesDraft((current) => ({
                          ...current,
                          [email]: current[email] || { ...defaultPolicyDraft },
                        }));
                        setNewPolicyEmail('');
                      }}
                      style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '9px 11px', background: '#FFFFFF', cursor: 'pointer' }}
                    >
                      Override hinzufügen
                    </button>
                  </div>

                  {Object.keys(userPoliciesDraft).length > 0 && (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {Object.entries(userPoliciesDraft).map(([email, policy]) => (
                        <div key={email} style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '10px', display: 'grid', gridTemplateColumns: '1fr 170px auto auto auto', gap: '8px', alignItems: 'center' }}>
                          <div style={{ fontSize: '12px', color: '#122A5C', wordBreak: 'break-all' }}>{email}</div>
                          <input
                            type="number"
                            min={1}
                            value={policy.maxContractsPerMonth}
                            onChange={(event) => {
                              const nextValue = Number(event.target.value || 1);
                              setUserPoliciesDraft((current) => ({
                                ...current,
                                [email]: {
                                  ...current[email],
                                  maxContractsPerMonth: nextValue,
                                },
                              }));
                            }}
                            style={{ border: '1px solid #CBD5E1', borderRadius: '10px', padding: '8px 10px' }}
                          />
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                            <input
                              type="checkbox"
                              checked={policy.canEditHtml !== false}
                              onChange={(event) => {
                                setUserPoliciesDraft((current) => ({
                                  ...current,
                                  [email]: {
                                    ...current[email],
                                    canEditHtml: event.target.checked,
                                  },
                                }));
                              }}
                            />
                            Edit
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                            <input
                              type="checkbox"
                              checked={policy.canDownloadPdfTemplate !== false}
                              onChange={(event) => {
                                setUserPoliciesDraft((current) => ({
                                  ...current,
                                  [email]: {
                                    ...current[email],
                                    canDownloadPdfTemplate: event.target.checked,
                                  },
                                }));
                              }}
                            />
                            PDF
                          </label>
                          <button
                            onClick={() => {
                              setUserPoliciesDraft((current) => {
                                const next = { ...current };
                                delete next[email];
                                return next;
                              });
                            }}
                            style={{ border: '1px solid #FECACA', color: '#B91C1C', borderRadius: '10px', padding: '8px 10px', background: '#FFFFFF', cursor: 'pointer' }}
                          >
                            Entfernen
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <button
                    onClick={handleSaveAdminSettings}
                    disabled={settingsLoading}
                    style={{ border: 'none', borderRadius: '10px', padding: '10px 14px', background: '#0F766E', color: '#FFFFFF', cursor: 'pointer', fontWeight: 700 }}
                  >
                    {settingsLoading ? 'Speichere...' : 'Admin-Einstellungen speichern'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
